import {
  User,
  Shield,
  Bell,
  HelpCircle,
  LogOut,
  ChevronRight,
  Star,
  Flame,
  Zap,
  Cloud,
  ArrowRight,
  Code,
  Share2,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useApp } from '../context';
import { useNavigate } from 'react-router-dom';
import { usePageAnimation } from '../lib/usePageAnimation';
import { authApi, dashboardApi, supportApi } from '../lib/api';
import { registerCacheCleanup, hasChanged } from '../lib/moduleCache';
import { canShare, shareApp } from '../lib/utils';
import packageJson from '../../package.json';
import ContactAvatar from './ContactAvatar';

// Use YYYY-MM-DD strings for comparison to avoid UTC vs local timezone bugs
function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00'); // noon to avoid DST edge cases
  d.setDate(d.getDate() + days);
  return toDateStr(d);
}

function computeLocalStreaks(interactions: { date: string }[]) {
  // Deduplicate and sort descending by local date string
  const dateStrs = [
    ...new Set(
      interactions.map((i) => {
        // interaction.date is YYYY-MM-DD or ISO string — extract local date
        const d = new Date(i.date);
        return toDateStr(d);
      }),
    ),
  ].sort((a, b) => (a > b ? -1 : a < b ? 1 : 0));

  if (dateStrs.length === 0) return { currentStreak: 0, longestStreak: 0 };

  const todayStr = toDateStr(new Date());
  const yesterdayStr = addDays(todayStr, -1);

  // Calculate all streaks (consecutive days) and find the longest
  let longest = 1;
  let run = 1;
  for (let i = 1; i < dateStrs.length; i++) {
    if (addDays(dateStrs[i], 1) === dateStrs[i - 1]) run++;
    else {
      if (run > longest) longest = run;
      run = 1;
    }
  }
  if (run > longest) longest = run;

  // Current streak must include today or yesterday
  if (dateStrs[0] !== todayStr && dateStrs[0] !== yesterdayStr) {
    return { currentStreak: 0, longestStreak: longest };
  }
  let curr = 1;
  for (let i = 1; i < dateStrs.length; i++) {
    if (addDays(dateStrs[i], 1) === dateStrs[i - 1]) curr++;
    else break;
  }
  return { currentStreak: curr, longestStreak: longest };
}

// Module-level cache so re-mounts don't flash null states
let _streaksCache: { currentStreak: number; longestStreak: number } | null = null;
let _referralCountCache: number | null = null;
let _unreadTicketsCache: number | null = null;
registerCacheCleanup(() => {
  _streaksCache = null;
  _referralCountCache = null;
  _unreadTicketsCache = null;
});

export default function Profile() {
  const navigate = useNavigate();
  const shouldAnimate = usePageAnimation();
  const { state, logout } = useApp();
  const isGuest = state.user?.isGuest;

  // Streak data: fetch from dashboard API (cached) for auth, compute locally for guest
  const [streaks, setStreaks] = useState<{
    currentStreak: number;
    longestStreak: number;
  } | null>(state.isGuestMode ? computeLocalStreaks(state.interactions) : _streaksCache);
  const [referralCount, setReferralCount] = useState<number | null>(_referralCountCache);
  const [unreadTickets, setUnreadTickets] = useState(_unreadTicketsCache || 0);

  useEffect(() => {
    if (state.isGuestMode) {
      setStreaks(computeLocalStreaks(state.interactions));
      return;
    }
    dashboardApi
      .get()
      .then((d) => {
        const s = {
          currentStreak: d.weeklyStats.currentStreak,
          longestStreak: d.weeklyStats.longestStreak,
        };
        if (hasChanged(_streaksCache, s)) setStreaks(s);
        _streaksCache = s;
      })
      .catch(() => setStreaks({ currentStreak: 0, longestStreak: 0 }));

    authApi
      .getReferrals()
      .then((r) => {
        if (_referralCountCache !== r.totalReferrals) setReferralCount(r.totalReferrals);
        _referralCountCache = r.totalReferrals;
      })
      .catch(() => {});

    supportApi
      .unreadCount()
      .then((r) => {
        if (_unreadTicketsCache !== r.count) setUnreadTickets(r.count);
        _unreadTicketsCache = r.count;
      })
      .catch(() => {});
  }, [state.isGuestMode, state.interactions]);

  const handleCreateAccount = () => {
    // [TODO/PLACEHOLDER]: Data migration logic here.
    window.dispatchEvent(new CustomEvent('open-login', { detail: { source: 'guest' } }));
  };

  // Show red dot on notifications if user has settings enabled but browser permission not granted
  const ns = state.notificationSettings;
  const hasAnyNotificationEnabled =
    ns.dailyReminders || ns.birthdayReminders || ns.weeklySummary || ns.overdueAlerts;
  const browserPermission = 'Notification' in window ? Notification.permission : 'unsupported';
  const showNotificationWarning = hasAnyNotificationEnabled && browserPermission !== 'granted';

  const DEVELOPER_ROLES = ['tester', 'admin', 'super_admin'];
  const hasDeveloperAccess = state.user?.roles?.some((r) => DEVELOPER_ROLES.includes(r)) ?? false;

  const menuItems = [
    {
      icon: User,
      label: 'Personal Info',
      path: '/personal-info',
      color: 'text-blue-500',
      bg: 'bg-blue-50',
      desc: 'Manage your profile',
    },
    {
      icon: Bell,
      label: 'Notifications',
      path: '/notification-settings',
      color: 'text-[#fa8585]',
      bg: 'bg-[#fa8585]/10',
      desc: 'Alerts & reminders',
      showBadge: showNotificationWarning,
    },
    {
      icon: Shield,
      label: 'Privacy & Security',
      path: '/privacy-security',
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
      desc: 'Secure your data',
    },
    {
      icon: HelpCircle,
      label: 'Support & Feedback',
      path: '/help-support',
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      desc: 'Get help or share feedback',
      showBadge: unreadTickets > 0,
    },
    ...(hasDeveloperAccess
      ? [
          {
            icon: Code,
            label: 'Developer',
            path: '/profile/developer',
            color: 'text-purple-500',
            bg: 'bg-purple-50',
            desc: 'Testing & mock data',
          },
        ]
      : []),
  ];

  if (isGuest) {
    return (
      <div className="bg-[#f8f5f5] min-h-screen pb-32">
        <header className="p-5 flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Settings</h1>
        </header>

        <div className="px-5 space-y-5">
          {/* Guest Hero Card */}
          <motion.div
            initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-amber-400 p-1.5 rounded-lg text-slate-900">
                  <Star size={16} fill="currentColor" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  Guest Mode
                </span>
              </div>
              <h2 className="text-2xl font-bold mb-2 leading-tight">
                Unlock the full Nudge experience
              </h2>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                Create an account to sync your data across devices and never lose your progress.
              </p>
              <button
                onClick={handleCreateAccount}
                className="inline-flex items-center gap-2 bg-[#fa8585] px-6 py-3 rounded-2xl font-bold text-sm hover:scale-105 transition-transform shadow-lg shadow-[#fa8585]/20"
              >
                Create Account <ArrowRight size={16} />
              </button>
            </div>
            {/* Decorative Background Element */}
            <div className="absolute -right-8 -bottom-8 size-48 bg-[#fa8585]/10 rounded-full blur-3xl"></div>
          </motion.div>

          {/* Benefits List */}
          <div className="bg-white rounded-[2.5rem] p-6 shadow-sm border border-slate-100">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6 px-2">
              Why create an account?
            </h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="size-12 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center shrink-0">
                  <Cloud size={24} />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Cloud Sync</p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Your relationships are too important to lose. We'll keep them safe on our secure
                    servers.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="size-12 rounded-2xl bg-[#fa8585]/10 text-[#fa8585] flex items-center justify-center shrink-0">
                  <Bell size={24} />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Smart Notifications</p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Get timely reminders to reach out, birthdays, and special milestones even when
                    the app is closed.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="size-12 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
                  <Zap size={24} />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Totally Free</p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Nudge is and will always be free to use. No hidden costs or annoying
                    subscriptions.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="size-12 rounded-2xl bg-emerald-50 text-emerald-500 flex items-center justify-center shrink-0">
                  <Shield size={24} />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Privacy First</p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Your data is encrypted and never shared with third parties. Your contacts and
                    personal information stay private.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] mt-8">
            Version {packageJson.version} • Nudge App
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f8f5f5] min-h-screen pb-24">
      {/* Header */}
      <header className="p-5">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Settings</h1>
      </header>

      <div className="px-5 space-y-5">
        {/* User Hero Card */}
        <motion.div
          initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl"
        >
          <div className="relative z-10 flex flex-col items-center text-center">
            <div className="relative">
              <div className="size-24 rounded-[2rem] overflow-hidden ring-4 ring-white/10 shadow-2xl">
                <ContactAvatar
                  photoUrl={state.user?.photoUrl}
                  name={state.user?.name || ''}
                  size="xl"
                  className="size-full rounded-4xl!"
                />
              </div>
              <div className="absolute -bottom-2 -right-2 bg-[#fa8585] text-white p-2 rounded-2xl border-4 border-slate-900">
                <Zap size={16} fill="white" />
              </div>
            </div>
            <h2 className="mt-6 text-2xl font-bold">{state.user?.name}</h2>
            <p className="text-slate-400 text-sm font-medium">{state.user?.email}</p>
          </div>
          {/* Decorative Background Element */}
          <div className="absolute -right-12 -top-12 size-48 bg-[#fa8585]/10 rounded-full blur-3xl"></div>
          <div className="absolute -left-12 -bottom-12 size-48 bg-blue-500/10 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Stats Bento Grid */}
        <div className="grid grid-cols-2 gap-3">
          <motion.div
            initial={shouldAnimate ? { opacity: 0, scale: 0.9, z: 0 } : false}
            animate={{ opacity: 1, scale: 1, z: 0 }}
            transition={shouldAnimate ? { delay: 0.1 } : undefined}
            className="bg-white rounded-[2rem] p-4 shadow-sm border border-slate-100 relative z-10 flex items-center gap-3"
          >
            <div className="bg-blue-50 p-2 rounded-xl text-blue-500 shrink-0">
              <Star size={18} fill="currentColor" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 leading-none">
                {state.contacts.length}
              </p>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                Contacts
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={shouldAnimate ? { opacity: 0, scale: 0.9, z: 0 } : false}
            animate={{ opacity: 1, scale: 1, z: 0 }}
            transition={shouldAnimate ? { delay: 0.2 } : undefined}
            className="bg-white rounded-[2rem] p-4 shadow-sm border border-slate-100 relative z-10 flex items-center gap-3"
          >
            <div
              className={`p-2 rounded-xl shrink-0 ${streaks === null ? 'bg-slate-100 animate-pulse' : streaks.currentStreak > 0 ? 'bg-orange-50 text-orange-500' : 'bg-slate-50 text-slate-300'}`}
            >
              <Flame size={18} className={streaks === null ? 'opacity-0' : ''} />
            </div>
            <div>
              <AnimatePresence mode="wait">
                {streaks === null ? (
                  <motion.div
                    key="streak-skeleton"
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="h-6 w-8 bg-slate-100 rounded animate-pulse" />
                    <div className="h-3 w-16 bg-slate-100 rounded animate-pulse mt-1" />
                    <div className="h-2.5 w-12 bg-slate-100 rounded animate-pulse mt-0.5" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="streak-data"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-2xl font-bold text-slate-900 leading-none">
                      {streaks.currentStreak}
                    </p>
                    <p className="text-[9px] font-bold uppercase tracking-widest mt-0.5">
                      <span className="text-slate-400">Day streak</span>
                      {streaks.longestStreak > 0 && (
                        <>
                          <br />
                          <span className="text-slate-300">Best {streaks.longestStreak}</span>
                        </>
                      )}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>

        {/* Settings Menu Bento */}
        <div className="bg-white rounded-[2rem] p-4 shadow-sm border border-slate-100 mb-8">
          <div className="space-y-1">
            {menuItems.map((item, idx) => (
              <motion.button
                key={idx}
                onClick={() => navigate(item.path || '#')}
                initial={shouldAnimate ? { opacity: 0, x: -10 } : false}
                animate={{ opacity: 1, x: 0 }}
                transition={shouldAnimate ? { delay: 0.3 + idx * 0.1 } : undefined}
                whileTap={{ scale: 0.98 }}
                className="w-full py-3 px-2 rounded-2xl flex items-center gap-4 hover:bg-slate-50 transition-colors group"
              >
                <div className="relative shrink-0">
                  <div
                    className={`size-11 rounded-[14px] ${item.bg} ${item.color} flex items-center justify-center`}
                  >
                    <item.icon size={20} />
                  </div>
                  {'showBadge' in item && item.showBadge && (
                    <div className="absolute -top-0.5 -right-0.5 size-3 bg-red-500 rounded-full border-2 border-white" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold text-[15px] text-slate-900">{item.label}</p>
                  <p className="text-[12px] text-slate-400 mt-0.5">{item.desc}</p>
                </div>
                <ChevronRight
                  size={18}
                  className="text-slate-300 group-hover:text-slate-400 transition-colors"
                />
              </motion.button>
            ))}

            {/* Share App */}
            {canShare() && (
              <motion.button
                initial={shouldAnimate ? { opacity: 0, x: -10 } : false}
                animate={{ opacity: 1, x: 0 }}
                transition={shouldAnimate ? { delay: 0.8 } : undefined}
                whileTap={{ scale: 0.98 }}
                onClick={() => shareApp(state.user?.referralCode)}
                className="w-full py-3 px-2 rounded-2xl flex items-center gap-4 hover:bg-slate-50 transition-colors group"
              >
                <div className="size-11 rounded-[14px] bg-[#fa8585]/10 text-[#fa8585] flex items-center justify-center shrink-0">
                  <Share2 size={20} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold text-[15px] text-slate-900">Share Nudge</p>
                  <p className="text-[12px] text-slate-400 mt-0.5">
                    {referralCount !== null && referralCount > 0
                      ? `${referralCount} friend${referralCount === 1 ? '' : 's'} joined`
                      : 'Invite friends to stay connected'}
                  </p>
                </div>
                <ChevronRight
                  size={18}
                  className="text-slate-300 group-hover:text-slate-400 transition-colors"
                />
              </motion.button>
            )}

            {/* Sign Out button removed from here */}
          </div>
        </div>

        {/* Sign Out */}
        <motion.button
          initial={shouldAnimate ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={shouldAnimate ? { delay: 0.9 } : undefined}
          whileTap={{ scale: 0.98 }}
          onClick={logout}
          className="w-full bg-red-50 rounded-[2rem] p-4 flex items-center justify-between group"
        >
          <div className="flex items-center gap-4">
            <div className="size-11 rounded-[14px] bg-white text-red-500 flex items-center justify-center shadow-sm">
              <LogOut size={20} />
            </div>
            <div className="text-left">
              <p className="font-bold text-[15px] text-red-600">Sign Out</p>
              <p className="text-[12px] text-red-400 mt-0.5">Exit your account</p>
            </div>
          </div>
          <ChevronRight
            size={18}
            className="text-red-200 group-hover:text-red-300 transition-colors"
          />
        </motion.button>

        <p className="text-center text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] mt-8">
          Version {packageJson.version} • Nudge App
        </p>
      </div>
    </div>
  );
}
